/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import {
  EuiButtonGroup,
  EuiEmptyPrompt,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHorizontalRule,
  EuiIcon,
  EuiPanel,
  EuiSearchBar,
  EuiSkeletonText,
  EuiSpacer,
  Query,
  SearchFilterConfig,
} from "@inscopix/ideas-eui";
import { Tool, useAllToolsQuery } from "./ToolBrowser.api";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { ToolCard } from "./ToolCard";
import { CallOutError } from "components/CallOutError/CallOutError";
import { VList } from "virtua";
import { PromptFirstCustomTool } from "./PromptFirstCustomTool";
import { isNonNull } from "utils/isNonNull";
import { UserAvatar } from "components/UserAvatar/UserAvatar";
import { chain, orderBy } from "lodash";
import { ToolVisibility } from "types/constants";
import {
  ToggleFilter,
  ToggleFilterProps,
} from "components/ProjectsCards/ToggleFilter";
import { useIdeasSearchParams } from "hooks/useIdeasSearchParams";
import { captureException } from "@sentry/react";
import { useEuiSearchBar } from "hooks/useEuiSearchBar";

const items = [
  { name: "Date Created", value: "dateCreated", first: "desc" },
  { name: "Name", value: "name", first: "asc" },
] as const;

interface SortFilterProps {
  selected: ToggleFilterProps<typeof items>["selected"];
  onChange: ToggleFilterProps<typeof items>["onChange"];
}

const SortFilter = ({ selected, onChange }: SortFilterProps) => {
  return <ToggleFilter items={items} selected={selected} onChange={onChange} />;
};

type Scope = "custom" | "all";

interface ScopeFilterProps {
  scope: Scope;
  setScope: (scope: Scope) => void;
}

const ScopeFilter = ({ scope, setScope }: ScopeFilterProps) => {
  return (
    <EuiButtonGroup
      legend="Scope filter"
      options={[
        {
          id: "all",
          label: "All",
          css: css`
            border-start-end-radius: 0px !important;
            border-end-end-radius: 0px !important;
          `,
        },
        { id: "custom", label: "Custom" },
      ]}
      idSelected={scope}
      onChange={(scope) => setScope(scope as Scope)}
      buttonSize="m"
    />
  );
};

const styles = {
  root: css`
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 24px;
  `,
};

const QUERY_FIELDS = {
  author: "user.email",
  tags: "tags" satisfies keyof Tool,
} as const;
interface ToolBrowserProps {
  tenantId: number;
  tenantKey: string;
}

export const ToolBrowser = ({ tenantId, tenantKey }: ToolBrowserProps) => {
  const { setParam, getParam } = useIdeasSearchParams();
  const { data, isLoading, error } = useAllToolsQuery();
  // const [query, setQuery] = useState<Query>(Query.parse(""));
  const { query, onChange, searchBarRef } = useEuiSearchBar();
  const [scope, setScope] = useState<Scope>("all");
  const [sort, setSort] = useState<ToggleFilterProps<typeof items>["selected"]>(
    { item: "dateCreated", state: "desc" },
  );
  const isCustomTool = data?.some((tool) => tool.tenant?.id === tenantId);

  const allVisibleTools = useMemo(
    () =>
      (data ?? []).filter(
        (tool) =>
          tool.visibility === ToolVisibility.PROTECTED ||
          tool.visibility === ToolVisibility.PUBLIC ||
          tool.tenant?.id === tenantId,
      ),
    [data, tenantId],
  );

  const filteredTools = useMemo(() => {
    // Remove tools from other tenants
    let tools = allVisibleTools;

    if (query) {
      tools = Query.execute(query, tools);
    }

    const filteredTools = tools.filter((tool) => {
      switch (scope) {
        case "custom":
          return tool.visibility === ToolVisibility.PRIVATE;
        default:
          return true;
      }
    });

    switch (sort.item) {
      case "dateCreated":
        return orderBy(
          filteredTools,
          (tool) => new Date(tool.date_created).getTime(),
          sort.state,
        );
      case "name":
        return orderBy(
          filteredTools,
          (tool) => tool.name.toLocaleLowerCase(),
          sort.state,
        );
    }
  }, [allVisibleTools, query, scope, sort.item, sort.state]);

  const onChangeSort = useCallback(
    (selected: ToggleFilterProps<typeof items>["selected"]) => {
      setParam("SORT_FIELD", selected.item);
      setParam("SORT_ORDER", selected.state);
      setSort(selected);
    },
    [setParam],
  );

  const searchFilters: SearchFilterConfig[] = useMemo(() => {
    if (data === undefined) {
      // Defer registering filters until data loads
      // If this is done before options are populated eui won't display the pills
      // for filters restored from the route until the dropdown is opened
      return [];
    }
    return [
      {
        type: "custom_component",
        component: () => (
          <ScopeFilter
            scope={scope}
            setScope={(scope) => {
              setParam("TOOL_AND_CONTAINER_SCOPE", scope);
              setScope(scope);
            }}
          />
        ),
      },
      {
        type: "field_value_selection",
        field: QUERY_FIELDS.author,
        name: "Author",
        multiSelect: "or",
        options: chain(allVisibleTools)
          .map((tool) => tool.user)
          .filter(isNonNull)
          .uniqBy((user) => user.id)
          .map((user) => {
            const fullName = `${user.first_name} ${user.last_name}`;
            return {
              value: user.email,
              name: fullName,
              view: <UserAvatar userId={user.id} displayName size="s" />,
            };
          })
          .value(),
      },
      {
        type: "field_value_selection",
        field: QUERY_FIELDS.tags,
        name: "Tag",
        multiSelect: "or",

        options: chain(allVisibleTools)
          .flatMap((c) => c.tags)
          .filter(isNonNull)
          .uniq()
          .map((tag) => {
            return {
              value: tag,
              name: tag,
            };
          })
          .value(),
      },
      {
        type: "custom_component",
        component: () => <SortFilter selected={sort} onChange={onChangeSort} />,
      },
    ];
  }, [allVisibleTools, data, onChangeSort, scope, setParam, sort]);

  /**
   * Load sort route params into state on mount
   */
  useEffect(() => {
    const sortField = getParam("SORT_FIELD");
    const sortOrder = getParam("SORT_ORDER");
    if (sortField !== null && sortOrder !== null) {
      const selectedItem = items.find((item) => item.value === sortField);

      if (selectedItem !== undefined) {
        setSort((prev) => {
          // prevent re render when reading params that were just set
          if (prev.item !== sortField || prev.state !== sortOrder) {
            return { item: selectedItem.value, state: sortOrder };
          }
          return prev;
        });
      } else {
        captureException("Failed to restore route params in Tool Browser");
      }
    }
  }, [getParam]);

  /**
   * Restore All/Custom from route
   */
  useEffect(() => {
    const scopeRoute = getParam("TOOL_AND_CONTAINER_SCOPE");
    if (scopeRoute !== null) {
      setScope(scopeRoute);
    }
  }, [getParam]);

  return (
    <EuiFlexGroup css={styles.root} direction="column" gutterSize="l">
      <EuiFlexItem grow={false}>
        <EuiSearchBar
          ref={searchBarRef}
          query={query}
          onChange={onChange}
          filters={searchFilters}
          box={{
            placeholder: "Find a tool...",
            incremental: false,
          }}
        />
      </EuiFlexItem>

      <EuiFlexItem style={{ overflow: "hidden" }}>
        {(() => {
          if (isLoading) {
            return <EuiSkeletonText lines={5} />;
          } else if (error) {
            return <CallOutError />;
          } else if (scope === "custom" && !isCustomTool) {
            return (
              <PromptFirstCustomTool
                tenantId={tenantId}
                tenantKey={tenantKey}
              />
            );
          } else if (filteredTools.length === 0) {
            return (
              <EuiPanel hasBorder>
                <EuiEmptyPrompt
                  icon={<EuiIcon type="indexMapping" size="xl" />}
                  title={<h2>No tools found</h2>}
                  titleSize="m"
                />
              </EuiPanel>
            );
          } else {
            const pinnedTools = filteredTools.filter((tool) => tool.pinned);
            const unpinnedTools = filteredTools.filter((tool) => !tool.pinned);

            return (
              <VList style={{ height: "100%" }}>
                {pinnedTools.map((tool, idx, arr) => {
                  const isLastItem = idx === arr.length - 1;
                  return (
                    <Fragment key={tool.id}>
                      <ToolCard {...tool} onDelete={() => undefined} />
                      {!isLastItem && <EuiSpacer />}
                    </Fragment>
                  );
                })}

                {pinnedTools.length > 0 && unpinnedTools.length > 0 && (
                  <EuiHorizontalRule />
                )}

                {unpinnedTools.map((tool, idx, arr) => {
                  const isLastItem = idx === arr.length - 1;
                  return (
                    <Fragment key={tool.id}>
                      <ToolCard {...tool} onDelete={() => undefined} />
                      {!isLastItem && <EuiSpacer />}
                    </Fragment>
                  );
                })}
              </VList>
            );
          }
        })()}
      </EuiFlexItem>
    </EuiFlexGroup>
  );
};
